// Dev-only activation of a measured dial cell, for hand-played feel passes.
//
// WHY THIS EXISTS
// The chunk-5 dial knobs (`_demandDials`, `_softCap.boardTerm/boardPin`, the
// tier-map levels) are module-scope exports written only by diagnostic probes.
// A probe can measure what a cell does to 600 seeded drafts; it cannot tell you
// whether the top of the board FEELS right to play. This makes one measured
// cell reachable from a local dev session so that question can be answered by
// hand, before any default moves.
//
// SAFETY MODEL — three independent layers, in order:
//   1. `process.env.NODE_ENV !== "production"` is the FIRST statement of every
//      exported function, written inline rather than behind a helper call, so
//      CRA's DefinePlugin substitutes a literal `false` and the minifier drops
//      the entire body. Every cell definition and the param string itself live
//      INSIDE those guards for the same reason — a production bundle contains
//      no activation path and no "aug7cell" string to find. Verified by
//      grepping the built bundle, not asserted.
//   2. Opt-in per page load via a URL param. No localStorage, no persistence:
//      close the tab and the session is stock again.
//   3. An unmissable on-screen badge whenever a cell is live, plus a `dials`
//      field on the persisted draft record, so a dial'd draft can never be
//      mistaken for stock — by Ben at the time, or by anything reading the
//      record later.
//
// NOT A SHIPPING PATH. Nothing here changes a default. When a cell does ship,
// this module's cell entry becomes wrong (the "old" behaviour would then be the
// dial'd one) and should be deleted rather than updated.
//
// STATUS (chunk 9, Aug 8 2026): the registry is EMPTY. `aug7cell` shipped, so
// its entry was deleted per the rule above. A defaults session therefore has no
// active cell, no badge and a null `dials` stamp — which is the point: now that
// stock IS the accepted cell, a stock run must not be labelled as an override.
//
//   npm start  ->  http://localhost:3000/?dials=<cell>   (no cells defined today)

import { _demandDials } from "../engine/simulation";
import { _softCap } from "../engine/auction";
import { _applyTierMap } from "../data/tiers";

// Resolved once at startup; null in production and whenever no param is given.
let activeCell = null;

/**
 * Reads the activation param and applies the named cell. Call ONCE, before the
 * first render — it mutates module-scope engine knobs and is not idempotent in
 * any useful sense beyond that.
 *
 * Returns the active cell name, or null.
 */
export function activateDevDialsFromUrl() {
  if (process.env.NODE_ENV === "production") return null;
  if (typeof window === "undefined") return null;

  // EMPTY ON PURPOSE (chunk 9, Aug 8 2026). `aug7cell` lived here until the
  // cell SHIPPED — every one of its values is now an engine default, so the
  // entry was deleted rather than updated, exactly as the header says to do.
  //
  // Keeping it would have been actively wrong, not merely redundant: the dials
  // COMPOSE with the defaults they used to describe. `rscScale: 1.25` on a
  // shipped rbSpendCap of 75 gives 94, not 75, and `rbShift: 0.10` shifts a
  // second 10% off wr$ pairs that already carry the fold. A feel pass on
  // ?dials=aug7cell would have been a feel pass on a configuration nobody
  // measured, wearing the label of one that was accepted.
  //
  // The mechanism stays for the NEXT cell. Add an entry here, in the probe's
  // own units, and everything downstream — badge, `dials` record field,
  // analytics stamp — works unchanged. With the registry empty every param
  // falls through to the unknown-cell branch below and the session runs stock,
  // which is the correct answer now that stock IS the accepted cell.
  const CELLS = {};

  let name = null;
  try {
    name = new URLSearchParams(window.location.search).get("dials");
  } catch {
    return null;                       // malformed URL — stay stock
  }
  if (!name) return null;

  const cell = CELLS[name];
  if (!cell) {
    // Loud, because a typo'd param that silently ran stock would send Ben off
    // to judge the feel of the wrong configuration. Named separately when the
    // registry is empty: "unknown cell" reads like a typo, and after chunk 9
    // the likeliest reason someone lands here is that they reached for the
    // cell that shipped.
    const known = Object.keys(CELLS);
    console.error(
      known.length
        ? `[devDials] unknown cell "${name}". Known: ${known.join(", ")}. Running STOCK.`
        : `[devDials] no cells are defined — the aug7cell bundle SHIPPED (chunk 9), so stock IS ` +
          `that configuration. "${name}" does nothing. Running STOCK.`
    );
    return null;
  }

  _demandDials.rscScale = cell.rscScale;
  _demandDials.rbShift = cell.rbShift;
  _softCap.boardTerm = cell.boardTerm;
  _softCap.boardPin = cell.boardPin;
  _softCap.absCapFrac = cell.absCapFrac;
  _applyTierMap(cell.recut, { WR: cell.wrLevels });

  activeCell = cell;
  console.warn(
    `[devDials] ACTIVE: ${cell.label} — ${cell.note}\n` +
    `  rscScale ${cell.rscScale} · rbShift ${cell.rbShift} · boardTerm ${cell.boardTerm} · ` +
    `boardPin ${cell.boardPin} · WR T1 x${cell.wrLevels[1]} · WR T2 x${cell.wrLevels[2]} · ` +
    `absCapFrac ${cell.absCapFrac} · recut ${cell.recut}`
  );
  return cell.label;
}

/** Active cell name, or null. Null in production, always. */
export function activeDialsName() {
  if (process.env.NODE_ENV === "production") return null;
  return activeCell ? activeCell.label : null;
}

/**
 * Compact tag for the `dials` field on any record the session writes, so a
 * dial'd draft can be filtered out of analytics and hangdiag corpora later.
 * Null (not "stock") when inactive, so the field is absent from stock records
 * and every existing consumer is unaffected.
 */
export function activeDialsTag() {
  if (process.env.NODE_ENV === "production") return null;
  if (!activeCell) return null;
  const c = activeCell;
  return `${c.label}:rsc${c.rscScale},shift${c.rbShift},b${c.boardTerm},pin${c.boardPin},` +
         `wr1x${c.wrLevels[1]},wr2x${c.wrLevels[2]},cap${c.absCapFrac},recut${c.recut ? 1 : 0}`;
}
